import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  useFinalizeCOAImportMutation,
  useGetCOAImportStateQuery,
} from "store/apis/chartOfAccounts";
import { ImportCOAData } from "types/Models/books";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { ImportQBOHeader, useImportQBO } from "./ImportQBO";
import {
  useFinalizeQBOImportMutation,
  useGetQBOImportDefaultCategoriesQuery,
} from "store/apis/qbo";
import { REQUIRED_IMPORT_DEFAULT_CATEGORIES } from "constants/qboImportSteps";
import { HowItWorksModal } from "./HowItWorksModal";

type ReviewStepProps = {
  onNext: () => void;
  importProcessId: string;
  entityId: string;
};

const createColumn = createColumnHelper<ImportCOAData>();

export const COA_IMPORT_REVIEW_COLUMNS = [
  createColumn.accessor("name", {
    size: 20,
    header: "CATEGORY NAME",
    cell: (row) => {
      return (
        <div className="t-text-body-sm t-text-text-100">
          {row.getValue() || "-"}
        </div>
      );
    },
  }),

  createColumn.accessor("parent", {
    size: 20,
    header: "PARENT NAME",
    cell: (row) => {
      return (
        <div className="t-text-body-sm t-text-text-100">
          {row.getValue() || "-"}
        </div>
      );
    },
  }),

  createColumn.accessor("identifier", {
    size: 10,
    header: "CATEGORY ID",
    cell: (row) => (
      <div className="t-text-body-sm t-text-text-100">
        {row.getValue() || "-"}
      </div>
    ),
  }),

  createColumn.accessor("opening_balance", {
    size: 20,
    header: () => <div className="t-text-end">OPENING BALANCE</div>,
    cell: (row) => {
      if (row.getValue()) {
        return (
          <div className="t-text-end t-text-body-sm t-text-text-100">
            <AmountSuperScript amount={row.getValue()} />
          </div>
        );
      }

      return <div className="t-text-end t-text-body-sm t-text-text-100">-</div>;
    },
  }),

  createColumn.accessor("description", {
    size: 30,
    header: "DESCRIPTION",
    cell: (row) => (
      <div className="t-text-body-sm t-text-text-100">
        {row.getValue() || "-"}
      </div>
    ),
  }),
];

export const ReviewStep = ({
  onNext,
  entityId,
  importProcessId,
}: ReviewStepProps) => {
  const { isOpen, close } = useImportQBO();
  const { alertToast, info: infoToast } = useToast();
  const [finalizeQBOImport, { isLoading: isFinalizing }] =
    useFinalizeQBOImportMutation();

  const { data, isLoading, isSuccess } = useGetCOAImportStateQuery(
    {
      entityId,
      coaImportStateId: importProcessId,
    },
    {
      skip: !importProcessId || !entityId,
    }
  );

  const { data: defaultCategories } = useGetQBOImportDefaultCategoriesQuery(
    { entityId },
    { skip: !entityId }
  );

  const { coa_data = [] } = data || {};
  const isEmpty = coa_data.length === 0;

  const hasMissingRequiredDefaults = (defaultCategories ?? []).some(
    (row) =>
      REQUIRED_IMPORT_DEFAULT_CATEGORIES.includes(row.category_name) &&
      !row.category
  );
  const isImportDisabled =
    isEmpty || isFinalizing || !defaultCategories || hasMissingRequiredDefaults;

  const table = useReactTable({
    data: coa_data,
    columns: COA_IMPORT_REVIEW_COLUMNS,
    getRowId: (row) => row.identifier.toString(),
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const onImport = async () => {
    try {
      await finalizeQBOImport({
        entityId,
      }).unwrap();
      infoToast({
        title: "Importing",
        message: "We'll update you once done",
      });
      onNext();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={() => close({ takeConfirmation: true })}
      modal={false}
    >
      <Modal.Content size="xxl" useCustomOverlay>
        <ImportQBOHeader />
        <Modal.Body className="t-flex t-flex-col t-gap-4 sm:t-px-4">
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={isEmpty}
          >
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success>
              <Banner
                type="neutral"
                description="The opening balances shown below might differ from the imported file as Inkle Books and Quickbooks handle opening balances differently."
              />
              <TableUI table={table} contentClassName="sm:t-min-w-[600px]" />
            </Async.Success>
          </Async.Root>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <HowItWorksModal />
          <Modal.RawClose asChild>
            <Button disabled={isFinalizing}>Cancel</Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={onImport}
            isLoading={isFinalizing}
            disabled={isImportDisabled}
          >
            Import
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
